import React, { useMemo } from 'react'
import { useDispatch } from 'react-redux'
import * as Yup from 'yup'
import { isEmpty, omitBy, startCase } from 'lodash'

import { BasicModal } from '@/components/layout/modals/BasicModal'
import { AbstractForm } from '@/components/forms/formik/AbstractForm'
import { closeModal } from '@/reducers/ui/ui.redux'
import { createContact, updateContact } from '@/reducers/outbound/outboundContactLists.actions'

import { TIMEZONE_OPTIONS } from '../Campaigns/ScheduleRetryPage'

// Matches the core-service phone_number validator (^\+?\d{10,15}$)
const PHONE_NUMBER_RE = /^\+?\d{10,15}$/

const STANDARD_FIELDS = ['phone_number', 'first_name', 'last_name', 'timezone']

export const ContactFormModal = ({ contact, contactListId, customFieldKeys = [], onSuccess }) => {
  const dispatch = useDispatch()
  const isEditing = !!contact

  // Preserve an existing timezone that isn't in the curated list rather than dropping it on save
  const timezoneOptions = useMemo(() => {
    const existing = contact?.timezone
    if (existing && !TIMEZONE_OPTIONS.some((option) => option.value === existing)) {
      return [...TIMEZONE_OPTIONS, { value: existing, label: existing }]
    }
    return TIMEZONE_OPTIONS
  }, [contact])

  const schema = useMemo(
    () => [
      {
        name: 'phone_number',
        label: 'Phone Number',
        type: 'text',
        required: true,
        customValidators: [
          Yup.string()
            .matches(PHONE_NUMBER_RE, 'Enter a valid phone number')
            .required('Phone number is required'),
        ],
      },
      { name: 'first_name', label: 'First Name', type: 'text' },
      { name: 'last_name', label: 'Last Name', type: 'text' },
      {
        name: 'timezone',
        label: 'Timezone',
        type: 'select',
        options: timezoneOptions,
        placeholder: 'Select Timezone',
      },
      ...customFieldKeys.map((key) => ({ name: key, label: startCase(key), type: 'text' })),
    ],
    [customFieldKeys, timezoneOptions]
  )

  const existingValues = useMemo(() => {
    if (!isEditing) {
      return undefined
    }
    return STANDARD_FIELDS.concat(customFieldKeys).reduce(
      (acc, key) => ({ ...acc, [key]: contact[key] ?? '' }),
      {}
    )
  }, [contact, customFieldKeys, isEditing])

  const handleClose = () => {
    dispatch(closeModal())
  }

  const handleSubmit = async (values) => {
    const standardFields = {
      phone_number: values.phone_number,
      first_name: values.first_name,
      last_name: values.last_name,
      timezone: values.timezone,
    }
    const customFields = customFieldKeys.reduce((acc, key) => ({ ...acc, [key]: values[key] }), {})
    const isBlank = (value) => value === ''
    // Drop blank custom fields so an edit doesn't write empty keys (e.g. a sibling
    // contact's field) onto this contact — core-service replaces the whole blob.
    const filledCustomFields = omitBy(customFields, isBlank)

    let response
    if (isEditing) {
      response = await dispatch(
        updateContact(contact.id, { ...standardFields, custom_fields: filledCustomFields })
      )
    } else {
      const payload = omitBy(standardFields, isBlank)
      if (!isEmpty(filledCustomFields)) {
        payload.custom_fields = filledCustomFields
      }
      response = await dispatch(createContact(contactListId, payload))
    }

    if (response) {
      onSuccess()
      handleClose()
    }
  }

  return (
    <BasicModal
      show
      title={isEditing ? 'Edit Contact' : 'Add Contact'}
      size="small"
      onClose={handleClose}
      canCloseOnDimmerClick={false}
      closeOnEscape={false}
    >
      <AbstractForm
        schema={schema}
        existingValues={existingValues}
        onSubmit={handleSubmit}
        buttonLabel={isEditing ? 'Save' : 'Add Contact'}
        isModal
      />
    </BasicModal>
  )
}
